import type { CompanyUser } from '@/types/integrations';

/** Excludes users the backend or demo data identifies as inactive or deleted. */
export function isCompanyUserAvailable(user: Pick<CompanyUser, 'status'>): boolean {
  const status = user.status?.trim().toLowerCase();
  return status !== 'inactive' && status !== 'deleted';
}

/** Matches a company user against a free-text query on name and role. */
export function filterUsersByNameOrRole<T extends Pick<CompanyUser, 'name' | 'role'>>(
  users: T[],
  query: string,
): T[] {
  const term = query.trim().toLowerCase();
  if (!term) return users;
  return users.filter(
    (u) => u.name.toLowerCase().includes(term) || u.role.toLowerCase().includes(term),
  );
}

/**
 * Matches a role member against a free-text query on name, email and status.
 * Status matches on a prefix so "active" does not also select inactive members.
 */
export function filterUsersByNameEmailOrStatus<
  T extends { name: string; email: string; status: string },
>(users: T[], query: string): T[] {
  const term = query.trim().toLowerCase();
  if (!term) return users;
  return users.filter(
    (u) =>
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term) ||
      u.status.toLowerCase().startsWith(term),
  );
}

/**
 * Keeps the first entry per person, matching on the normalised email and falling
 * back to the id for entries without one. Guards the user lists against a person
 * held under more than one resource record and against a list that combines saved
 * and pending users.
 */
export function dedupeUsers<T extends Pick<CompanyUser, 'id' | 'email'>>(users: T[]): T[] {
  const seen = new Set<string>();
  return users.filter((u) => {
    const key = u.email?.trim().toLowerCase() || `id:${u.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Matches a company user against a free-text query on name, email and role. */
export function filterUsersByNameEmailOrRole<
  T extends Pick<CompanyUser, 'name' | 'email' | 'role'>,
>(users: T[], query: string): T[] {
  const term = query.trim().toLowerCase();
  if (!term) return users;
  return users.filter(
    (u) =>
      u.name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term) ||
      u.role.toLowerCase().includes(term),
  );
}
